import { useEffect, useRef, useState } from 'react'
import { Box, Divider, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import { ScorePicker } from './ScorePicker'
import {
  COL_DELIVERABLE,
  COL_SELF_ASSESSMENT,
  COL_TEACHER,
  ROW_COMPACT_BREAKPOINT,
  ROW_SECTION_GAP,
} from './StudentAssessmentRow'

interface BulkAssessRowProps {
  onChange: (score: number) => void
  /** Same flag as the StudentAssessmentRow so the columns line up. */
  showDeliverableColumn: boolean
  disabled?: boolean
  color?: 'green' | 'blue'
  /** Rubric text per score ("1".."5") shown on hover (AR-828). */
  rubrics?: Record<string, string>
}

const FLASH_MS = 1500

/**
 * Bulk-assess line at the top of an item card whenever there's more than one
 * visible student. Mirrors the flex layout of StudentAssessmentRow so the
 * picker sits exactly above the per-student Teacher Assessment column.
 *
 * The picker is action-only (no persistent state) — we briefly flash the
 * clicked hand as selected for 1.5s to give visual confirmation.
 */
export function BulkAssessRow({
  onChange,
  showDeliverableColumn,
  disabled,
  color = 'green',
  rubrics,
}: BulkAssessRowProps) {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(ROW_COMPACT_BREAKPOINT)
  const [flashScore, setFlashScore] = useState<number | undefined>(undefined)
  const flashTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (flashTimeout.current) clearTimeout(flashTimeout.current)
  }, [])

  const handleChange = (score: number) => {
    setFlashScore(score)
    if (flashTimeout.current) clearTimeout(flashTimeout.current)
    flashTimeout.current = setTimeout(() => setFlashScore(undefined), FLASH_MS)
    onChange(score)
  }

  const bulkContent = (
    <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
      <Text size="xs" color="dimmed" weight={500} style={{ whiteSpace: 'nowrap' }}>
        {t('arc_teacher_assessment_drawer.bulk_assess')}:
      </Text>
      <ScorePicker
        value={flashScore}
        onChange={handleChange}
        disabled={disabled}
        size={isMobile ? 48 : 56}
        position="left"
        color={color}
        rubrics={rubrics}
      />
    </Box>
  )

  if (isMobile) {
    return (
      <>
        <Divider />
        <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>{bulkContent}</Box>
      </>
    )
  }

  return (
    <>
      <Divider />
      <Box
        data-testid="bulk-assess-row-grid"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: ROW_SECTION_GAP,
          width: '100%',
        }}
      >
        {/* Mirror StudentAssessmentRow sections with empty spacers so the
            bulk picker sits exactly above the per-student teacher column. */}
        <Box style={{ flex: '1 1 0', minWidth: 0 }} />
        {showDeliverableColumn && <Box style={{ flex: `0 0 ${COL_DELIVERABLE}px` }} />}
        <Box style={{ flex: `0 0 ${COL_SELF_ASSESSMENT}px` }} />
        <Box style={{ flex: `0 0 ${COL_TEACHER}px` }}>{bulkContent}</Box>
      </Box>
    </>
  )
}
